import { toLonLat } from "ol/proj.js";
import { askAIAssistantService } from "./treeFinder.js";
import { aiTreeRecommenderService } from "./Services/treeRecommenderService.js";
import {
  GEOSERVER_BASE_URL,
  GEOSERVER_WORKSPACE,
  OVERPASS_API_URL,
  METEO_ARCHIVE_BASE_URL,
  METEO_CLIMATE_BASE_URL,
  SOILGRIDS_BASE_URL,
} from "./config/api.js";

/**
 * @param {*} coordinate
 */
export function fetchTreesData(coordinate, radius) {
  const url = buildTreesRequestUrl(coordinate, radius);
  return fetch(url)
    .then((response) => response.json())
    .then((data) => {
      return data?.features ?? [];
    })
    .catch((error) => {
      console.error("❌ Fehler beim Abrufen der Bäume:", error);
      return []; // Im Fehlerfall leeres Array zurückgeben
    });
}

/**
 *
 * @param {*} lat
 * @param {*} lon
 */
export async function fetchGeologicalData(lat, lon) {
  const url = buildSoilApiUrl(lat, lon);

  try {
    const data = await fetch(url).then(r => r.json());

    return extractSoilProperties(data);
  } catch (error) {
    console.error("❌ Fehler beim Abrufen der URL:", url);
    console.error("❌ Fehler beim Abrufen der Geologiedaten:", error);
    return null;
  }
}

/**
 *
 *
 * @param {*} lat
 * @param {*} lon
 */
export async function fetchBuildingDevelopment(lat, lon) {
  const url = buildOverpassApiUrl(lat, lon);

  try {
    const data = await fetch(url).then(r => r.json());
    return checkForBuildings(data);
  } catch (error) {
    console.error("❌ API-Fehler beim Abrufen der Gebäudedaten:", error);
    return false; // Bei Fehler: lieber "unbebaut" zurückgeben
  }
}

/**
 *
 * @param {*} coordinate
 * @param {*} radius
 * @return {*}
 */
function buildTreesRequestUrl(coordinate, radius) {
  const lonLat = toLonLat(coordinate);
  return (
    `${GEOSERVER_BASE_URL}ows?service=WFS&version=1.1.0` +
    `&request=GetFeature&typeName=${GEOSERVER_WORKSPACE}:treesimport` +
    `&outputFormat=application/json` +
    `&CQL_FILTER=DWITHIN(geokooridnaten, POINT(` +
    lonLat[1] + // Breitengrad
    " " +
    lonLat[0] + // Längengrad
    `),${ radius}, meters)`
  );
}

/**
 *
 * @param {*} lat
 * @param {*} lon
 * @return {*}
 */
function buildWeatherApiUrl(lat, lon, startDate, today) {
  // Dynamische URL
  const apiUrl = `${METEO_ARCHIVE_BASE_URL}/v1/era5?latitude=${lat}&longitude=${lon}&start_date=${formatDate(
    startDate
  )}&end_date=${formatDate(
    today
  )}&daily=soil_temperature_0_to_7cm_mean&timezone=Europe%2FBerlin`;
  return apiUrl;
}

/**
 *
 * @param {*} lat
 * @param {*} lon
 * @return {*}
 */
function buildClimateApiUrl(lat, lon) {
  const today = new Date();
  const endDate = formatDate(today);

  const startDate = new Date();
  startDate.setMonth(startDate.getMonth() - 6);

  return (
    `${METEO_CLIMATE_BASE_URL}/v1/climate?` +
    "latitude=" +
    lat +
    "&longitude=" +
    lon +
    "&start_date=" +
    formatDate(startDate) +
    "&end_date=" +
    endDate +
    "&models=EC_Earth3P_HR" +
    "&temperature_unit=celsius" +
    "&precipitation_unit=mm" +
    "&windspeed_unit=kmh" +
    "&timezone=auto" +
    "&daily=temperature_2m_mean,precipitation_sum,soil_moisture_0_to_10cm_mean"
  );
}

/**
 *
 * @param {*} lat
 * @param {*} lon
 * @return {*}
 */
function buildSoilApiUrl(lat, lon) {
  const url =
    `${SOILGRIDS_BASE_URL}/soilgrids/v2.0/properties/query` +
    "?lon=" +
    lon +
    "&lat=" +
    lat +
    "&property=phh2o" +
    "&property=clay" +
    "&property=silt" +
    "&property=sand" +
    "&property=bdod" +
    "&property=cfvo" +
    "&property=ocd" +
    "&property=wv0033" +
    "&property=wv1500" +
    "&depth=30-60cm";
  return url;

  //&property=phh2o,sand,silt,clay,wv0033,wv1500&depth=0-5cm,5-15cm,15-30cm,30-60cm,60-100cm,100-200cm
}

function buildOverpassApiUrl(lat, lon) {
  const query = `
    [out:json];
    (
      way["building"](around:25, ${lat}, ${lon});
      relation["building"](around:25, ${lat}, ${lon});
    );
    out body;
    >;
    out skel qt;
  `;

  return (
    `${OVERPASS_API_URL}?data=` + encodeURIComponent(query)
  );
}

function isValidHourlyData(hourly) {
  return (
    hourly &&
    Array.isArray(hourly.temperature_2m) &&
    Array.isArray(hourly.precipitation)
  );
}

// Temperatur-Auswertung
function analyzeTemperatures(temps) {
  const validTemps = temps.filter(isNumber);
  const avg = average(validTemps);
  const max = Math.max(...validTemps);
  const hotHours = validTemps.filter((t) => t >= 30).length;

  return {
    avg: round(avg),
    max: round(max),
    hotHours: hotHours,
  };
}

// Niederschlags-Auswertung
function analyzePrecipitation(precips) {
  const valid = precips.filter(isNumber);
  const total = valid.reduce((sum, p) => sum + p, 0);

  return {
    total: round(total),
  };
}

// Hilfsfunktionen
function isNumber(n) {
  return typeof n === "number" && !isNaN(n);
}

function average(arr) {
  if (!arr.length) return 0;
  return arr.reduce((sum, n) => sum + n, 0) / arr.length;
}

function round(n) {
  return Math.round(n * 10) / 10;
}

function formatDate(date) {
  return date.toISOString().split("T")[0];
}

function isValidClimateData(daily) {
  return (
    daily &&
    Array.isArray(daily.temperature_2m_mean) &&
    Array.isArray(daily.precipitation_sum) &&
    Array.isArray(daily.soil_moisture_0_to_10cm_mean)
  );
}

function calculateAverage(values) {
  const valid = values.filter(isNumber);
  if (!valid.length) return null;
  const sum = valid.reduce((acc, v) => acc + v, 0);
  return round(sum / valid.length);
}

function calculateSum(values) {
  const valid = values.filter(isNumber);
  return round(valid.reduce((acc, v) => acc + v, 0));
}

function extractSoilProperties(data) {
  /*const layers = data?.properties?.layers ?? [];

  return {
    ph: extractAndConvert(layers, "phh2o", 10), // z. B. 64 → 6.4
    clay: extractAndConvert(layers, "clay", 10), // g/kg → %
    silt: extractAndConvert(layers, "silt", 10), // g/kg → %
    sand: extractAndConvert(layers, "sand", 10), // g/kg → %
    bdod: extractAndConvert(layers, "bdod", 100), // cg/cm³ → g/cm³
    cfvo: extractAndConvert(layers, "cfvo", 10), // cm³/dm³ → %
    ocd: extractRaw(layers, "ocd"), // dg/dm³ → direkt
  };*/

  const layers = data?.properties?.layers ?? [];
  const byName = (n) => layers.find((l) => l.name === n);

  // Alle Tiefen eines Layers als [{depth,label,value}] mit d_factor-Skalierung
  const readDepthSeries = (name) => {
    const layer = byName(name);
    if (!layer) return [];
    const df = layer.unit_measure?.d_factor ?? 1;
    return (layer.depths || []).map((d) => ({
      depth: d.label,
      value:
        (d.values?.["Q0.5"] ?? null) === null ? null : d.values["Q0.5"] / df,
    }));
  };

  const sand = readDepthSeries("sand"); // %
  const silt = readDepthSeries("silt"); // %
  const clay = readDepthSeries("clay"); // %
  const ph = readDepthSeries("phh2o"); // -
  const wv0033 = readDepthSeries("wv0033"); // % Vol.-Wasser (falls vorhanden)
  const wv1500 = readDepthSeries("wv1500"); // % Vol.-Wasser (falls vorhanden)

  // PAWC je Tiefe nur, wenn beide Reihen vorhanden und Index deckungsgleich
  const pawc =
    wv0033.length && wv1500.length
      ? wv0033.map((d, i) => ({
          depth: d.depth,
          value:
            d.value != null && wv1500[i]?.value != null
              ? d.value - wv1500[i].value
              : null,
        }))
      : [];

  return {
    Bodenart: {
      sand: sand,
      silt: silt,
      clay: clay,
      unit: "%",
    },
    "pH-Wert": {
      ph: ph,
      unit: "-",
    },
    Durchwurzelbarkeit: null, // nicht Teil des SoilGrids-REST-Outputs
    Wasserhaltevermögen: {
      pawc: pawc.length ? pawc : null,
      wv0033: wv0033.length ? wv0033 : null,
      wv1500: wv1500.length ? wv1500 : null,
      unit: "% Volumenwasser",
    },
    Wärmehaushalt: null,
  };
}

function extractAndConvert(layers, name, divisor) {
  const mean = getMeanValue(layers, name);
  return typeof mean === "number" ? mean / divisor : null;
}

function extractRaw(layers, name) {
  const mean = getMeanValue(layers, name);
  return typeof mean === "number" ? mean : null;
}

function getMeanValue(layers, name) {
  const layer = layers.find((l) => l.name === name);
  return layer?.depths?.[0]?.values?.mean ?? null;
}

function checkForBuildings(data) {
  return Array.isArray(data?.elements) && data.elements.length > 0;
}

/**
 * Berechnet Klima-Durchschnittswerte aus Open-Meteo ERA5 Archiv
 * für die letzten 2 Jahre ab dem aktuellen Tag.
 *
 * Liefert Niederschlag, Sonneneinstrahlung, Frost, Windlast
 * und Bodentemperatur (0–7 cm).
 *
 * @param {number} lat - Breitengrad (z. B. 49.38…)
 * @param {number} lon - Längengrad (z. B. 6.97…)
 * @returns {Promise<Object>} - JSON mit Mittelwerten, Aggregaten und Einheiten
 */
export async function fetchClimateAverages(lat, lon) {
  // ===============================
  // 1) Hilfsfunktionen
  // ===============================

  // Datum formatieren (YYYY-MM-DD für API)
  const fmt = (d) => d.toISOString().split("T")[0];

  // Mittelwert berechnen, ignoriert null oder NaN
  const mean = (arr) => {
    const vals = arr.filter((v) => v !== null && !isNaN(v));
    if (!vals.length) return null;
    return vals.reduce((a, b) => a + b, 0) / vals.length;
  };

  // Summe berechnen, ignoriert null oder NaN
  const sum = (arr) => {
    const vals = arr.filter((v) => v !== null && !isNaN(v));
    if (!vals.length) return 0;
    return vals.reduce((a, b) => a + b, 0);
  };

  // Frosttage zählen (Tmin < 0°C)
  const countFrostDays = (tminArr) =>
    tminArr.filter((v) => v !== null && !isNaN(v) && v < 0).length;

  // ===============================
  // 2) Zeitraum bestimmen (letzte 1 Jahre)
  // ===============================

  const today = new Date(); // aktuelles Datum
  const startDate = new Date(); // Start: heute - 1 Jahre
  startDate.setFullYear(today.getFullYear() - 1);
  const timezone = "Europe/Berlin"; // Zeitzone für API

  // ===============================
  // 3) API-URL bauen
  // ===============================

  const params = new URLSearchParams({
    latitude: String(lat),
    longitude: String(lon),
    start_date: fmt(startDate),
    end_date: fmt(today),
    // tägliche Variablen, die wir brauchen
    daily: [
      "precipitation_sum", // Niederschlag [mm]
      "shortwave_radiation_sum", // Sonneneinstrahlung [MJ/m²]
      "temperature_2m_min", // Tmin (für Frosttage)
      "windspeed_10m_max", // max Wind [m/s]
      "windgusts_10m_max", // max Böen [m/s]
      "soil_temperature_0_to_7cm_mean", // Bodentemperatur [°C]
    ].join(","),
    timezone: encodeURIComponent(timezone),
  });

  // URL korrekt zusammensetzen
  const apiUrl =
    `${METEO_ARCHIVE_BASE_URL}/v1/era5?${params.toString()}`.replace(
      /%252F/g,
      "%2F"
    ); // fix für doppelt encodierte Slashes

  // ===============================
  // 4) API aufrufen
  // ===============================

  const res = await fetch(apiUrl);
  if (!res.ok) {
    throw new Error(`Open-Meteo HTTP ${res.status}`);
  }
  const data = await res.json();
  const daily = data.daily;

  // ===============================
  // 5) Rohdaten extrahieren
  // ===============================

  const precip = daily.precipitation_sum; // Niederschlag mm
  const swrad = daily.shortwave_radiation_sum; // Sonneneinstrahlung MJ/m²
  const tmin = daily.temperature_2m_min; // Tmin °C
  const wsMax = daily.windspeed_10m_max; // max Wind m/s
  const wgMax = daily.windgusts_10m_max; // max Böen m/s
  const st07 = daily.soil_temperature_0_to_7cm_mean; // Boden-Temp °C

  // ===============================
  // 6) Mittelwerte und Aggregate berechnen
  // ===============================

  // Mittelwerte
  const precipitation_mean_mm_per_day = mean(precip);
  const shortwave_radiation_mean_MJ_m2_day = mean(swrad);
  const t2m_min_mean_C = mean(tmin);
  const windspeed10m_max_mean_m_s = mean(wsMax);
  const windgusts10m_max_mean_m_s = mean(wgMax);
  const soil_temp_0_7cm_mean_C = mean(st07);

  // Summen & Zählungen
  const precipitation_total_mm = sum(precip); // Gesamtniederschlag mm
  const frost_days = countFrostDays(tmin); // Anzahl Frosttage
  const validDays = (daily.time || []).filter(
    (_, i) => tmin[i] !== null
  ).length;
  const frost_days_share = validDays ? frost_days / validDays : null; // Anteil %

  // ===============================
  // 7) Ergebnis als JSON zurückgeben
  // ===============================

  return {
    period: {
      start_date: fmt(startDate),
      end_date: fmt(today),
      valid_days: validDays,
    },
    location: {
      latitude: lat,
      longitude: lon,
      timezone: data.timezone || timezone,
    },
    means: {
      precipitation_mm_per_day: precipitation_mean_mm_per_day,
      shortwave_radiation_MJ_m2_day: shortwave_radiation_mean_MJ_m2_day,
      soil_temp_0_7cm_C: soil_temp_0_7cm_mean_C,
      t2m_min_C: t2m_min_mean_C,
      windspeed10m_max_m_s: windspeed10m_max_mean_m_s,
      windgusts10m_max_m_s: windgusts10m_max_mean_m_s,
    },
    aggregates: {
      precipitation_total_mm, // Gesamt Niederschlag in mm
      frost_days, // Anzahl Frosttage
      frost_days_share, // Anteil Frosttage (0–1)
    },
    units: {
      precipitation_mm_per_day: "mm",
      precipitation_total_mm: "mm",
      shortwave_radiation_MJ_m2_day: "MJ/m²",
      soil_temp_0_7cm_C: "°C",
      t2m_min_C: "°C",
      windspeed10m_max_m_s: "m/s",
      windgusts10m_max_m_s: "m/s",
      frost_days: "days",
      frost_days_share: "ratio",
    },
    source: "Open-Meteo ERA5 archive API",
    apiUrl, // zum Nachschauen/debuggen
  };
}

/**
 * Ruft Boden- und Nutzungsinformationen von OSM (Overpass API) ab
 * @param {number} lat - Breitengrad
 * @param {number} lon - Längengrad
 * @returns {Promise<Object>} JSON mit Verdichtung, Bodenbelag, Nutzung, Bebauung
 */
export async function getLandInfo(lat, lon) {
  // ==========================================================================
  // SCHRITT 1: ALLE DATEN IN EINER QUERY ABRUFEN
  // ==========================================================================

  const query = `
    [out:json][timeout:25];
    (
      /* BESTEHENDE ABFRAGEN */
      way(around:50,${lat},${lon})[surface];
      way(around:50,${lat},${lon})[landuse];
      way(around:50,${lat},${lon})[building];
      
      /* PLAUSIBILITÄTSPRÜFUNG - Wasserflächen */
      way["natural"="water"](around:50,${lat},${lon});
      way["waterway"](around:50,${lat},${lon});
      node["natural"="coastline"](around:50,${lat},${lon});
      
      /* PLAUSIBILITÄTSPRÜFUNG - Versiegelung (zusätzlich) */
      way["highway"](around:50,${lat},${lon});
      way["railway"](around:50,${lat},${lon});
      way["amenity"="parking"](around:50,${lat},${lon});
      
      /* PLAUSIBILITÄTSPRÜFUNG - Ungeeignete Natur */
      way["natural"="wetland"](around:50,${lat},${lon});
      way["natural"="bare_rock"](around:50,${lat},${lon});
      way["natural"="scree"](around:50,${lat},${lon});
      way["natural"="sand"](around:50,${lat},${lon});
      way["natural"="glacier"](around:50,${lat},${lon});
      
      /* PLAUSIBILITÄTSPRÜFUNG - Industrie/Militär */
      way["landuse"="industrial"](around:50,${lat},${lon});
      way["landuse"="quarry"](around:50,${lat},${lon});
      way["landuse"="landfill"](around:50,${lat},${lon});
      way["landuse"="military"](around:50,${lat},${lon});
      
      /* PLAUSIBILITÄTSPRÜFUNG - Warnung: Landwirtschaft */
      way["landuse"="farmland"](around:50,${lat},${lon});
      way["landuse"="meadow"](around:50,${lat},${lon});
      way["landuse"="orchard"](around:50,${lat},${lon});
      way["landuse"="vineyard"](around:50,${lat},${lon});
      
      /* PLAUSIBILITÄTSPRÜFUNG - Warnung: Wald */
      way["landuse"="forest"](around:50,${lat},${lon});
      way["natural"="wood"](around:50,${lat},${lon});
      
      /* PLAUSIBILITÄTSPRÜFUNG - Warnung: Öffentliche Anlagen */
      way["leisure"="park"](around:50,${lat},${lon});
      way["leisure"="garden"](around:50,${lat},${lon});
      way["leisure"="playground"](around:50,${lat},${lon});
    );
    out tags;
  `;

  const url =
    `${OVERPASS_API_URL}?data=` + encodeURIComponent(query);

  return fetch(url).then(r => r.json())
    .then((data) => {
      // ====================================================================
      // SCHRITT 2: PLAUSIBILITÄTSPRÜFUNG (zuerst!)
      // ====================================================================

      let plausibilitaet = {
        geeignet: true,
        status: "geeignet",
        warnung: null,
        fehler: null,
        kategorie: null,
        grund: null,
        gefundeneProbleme: [], // NEU: Liste aller gefundenen Probleme
        osmTags: null, // NEU: Die spezifischen OSM-Tags
      };

      if (data.elements && data.elements.length > 0) {
        // Prüfe auf "absolut ungeeignet" Kriterien
        for (const el of data.elements) {
          if (!el.tags) continue;

          // WASSERFLÄCHEN
          if (
            el.tags.natural === "water" ||
            el.tags.waterway ||
            el.tags.natural === "coastline"
          ) {
            plausibilitaet.geeignet = false;
            plausibilitaet.status = "absolut_ungeeignet";
            plausibilitaet.kategorie = "wasser";

            // Detaillierte Beschreibung basierend auf Tag
            if (el.tags.natural === "water") {
              const waterType = el.tags.water || "Gewässer";
              plausibilitaet.fehler = `Dieser Standort liegt auf einer Wasserfläche (${waterType})`;
              plausibilitaet.grund = `natural=water${
                el.tags.water ? `, water=${el.tags.water}` : ""
              }`;
            } else if (el.tags.waterway) {
              const waterwayType = el.tags.waterway;
              const waterwayNames = {
                river: "Fluss",
                stream: "Bach",
                canal: "Kanal",
                drain: "Entwässerungsgraben",
              };
              plausibilitaet.fehler = `Dieser Standort liegt an einem Wasserlauf (${
                waterwayNames[waterwayType] || waterwayType
              })`;
              plausibilitaet.grund = `waterway=${waterwayType}`;
            } else if (el.tags.natural === "coastline") {
              plausibilitaet.fehler = "Dieser Standort liegt an einer Küste";
              plausibilitaet.grund = "natural=coastline";
            }

            plausibilitaet.osmTags = el.tags;
            plausibilitaet.gefundeneProbleme.push({
              typ: "absolut_ungeeignet",
              kategorie: "wasser",
              beschreibung: plausibilitaet.fehler,
              tags: el.tags,
            });

            // ABBRUCH - Standort nicht geeignet
            return {
              success: false,
              error: plausibilitaet.fehler,
              Plausibilitaet: plausibilitaet,
              Verdichtung: null,
              Bodenbelag: null,
              Nutzung: null,
              Bebauung: null,
            };
          }

          // VERSIEGELTE FLÄCHEN (Gebäude, Straßen, etc.)
          if (
            el.tags.building ||
            el.tags.highway ||
            el.tags.railway ||
            el.tags.amenity === "parking"
          ) {
            plausibilitaet.geeignet = false;
            plausibilitaet.status = "absolut_ungeeignet";
            plausibilitaet.kategorie = "versiegelt";

            // Detaillierte Beschreibung basierend auf Tag
            if (el.tags.building) {
              const buildingType = el.tags.building;
              const buildingNames = {
                yes: "Gebäude",
                house: "Wohnhaus",
                residential: "Wohngebäude",
                commercial: "Geschäftsgebäude",
                industrial: "Industriegebäude",
                garage: "Garage",
              };
              plausibilitaet.fehler = `Dieser Standort ist bebaut (${
                buildingNames[buildingType] || buildingType
              })`;
              plausibilitaet.grund = `building=${buildingType}`;
            } else if (el.tags.highway) {
              const highwayType = el.tags.highway;
              const highwayNames = {
                motorway: "Autobahn",
                trunk: "Schnellstraße",
                primary: "Hauptstraße",
                secondary: "Landstraße",
                residential: "Wohnstraße",
                service: "Zufahrtsstraße",
                footway: "Fußweg",
                path: "Pfad",
              };
              plausibilitaet.fehler = `Dieser Standort liegt auf einer Straße (${
                highwayNames[highwayType] || highwayType
              })`;
              plausibilitaet.grund = `highway=${highwayType}`;
            } else if (el.tags.railway) {
              plausibilitaet.fehler = `Dieser Standort liegt auf Bahngleisen (${el.tags.railway})`;
              plausibilitaet.grund = `railway=${el.tags.railway}`;
            } else if (el.tags.amenity === "parking") {
              plausibilitaet.fehler = "Dieser Standort ist ein Parkplatz";
              plausibilitaet.grund = "amenity=parking";
            }

            plausibilitaet.osmTags = el.tags;
            plausibilitaet.gefundeneProbleme.push({
              typ: "absolut_ungeeignet",
              kategorie: "versiegelt",
              beschreibung: plausibilitaet.fehler,
              tags: el.tags,
            });

            // ABBRUCH - Standort nicht geeignet
            return {
              success: false,
              error: plausibilitaet.fehler,
              Plausibilitaet: plausibilitaet,
              Verdichtung: null,
              Bodenbelag: null,
              Nutzung: null,
              Bebauung: null,
            };
          }

          // UNGEEIGNETE NATURRÄUME
          if (
            el.tags.natural === "wetland" ||
            el.tags.natural === "bare_rock" ||
            el.tags.natural === "scree" ||
            el.tags.natural === "sand" ||
            el.tags.natural === "glacier"
          ) {
            plausibilitaet.geeignet = false;
            plausibilitaet.status = "absolut_ungeeignet";
            plausibilitaet.kategorie = "naturraum";

            // Detaillierte Beschreibung basierend auf Naturraum
            const naturTypes = {
              wetland: "Feuchtgebiet/Sumpf",
              bare_rock: "nackter Fels",
              scree: "Geröllfeld",
              sand: "Sandfläche/Düne",
              glacier: "Gletscher",
            };
            const naturType = el.tags.natural;
            plausibilitaet.fehler = `Dieser Standort liegt auf ungeeignetem Naturraum (${
              naturTypes[naturType] || naturType
            })`;
            plausibilitaet.grund = `natural=${naturType}`;

            plausibilitaet.osmTags = el.tags;
            plausibilitaet.gefundeneProbleme.push({
              typ: "absolut_ungeeignet",
              kategorie: "naturraum",
              beschreibung: plausibilitaet.fehler,
              tags: el.tags,
            });

            // ABBRUCH - Standort nicht geeignet
            return {
              success: false,
              error: plausibilitaet.fehler,
              Plausibilitaet: plausibilitaet,
              Verdichtung: null,
              Bodenbelag: null,
              Nutzung: null,
              Bebauung: null,
            };
          }

          // INDUSTRIE/MILITÄR
          if (
            el.tags.landuse === "industrial" ||
            el.tags.landuse === "quarry" ||
            el.tags.landuse === "landfill" ||
            el.tags.landuse === "military"
          ) {
            plausibilitaet.geeignet = false;
            plausibilitaet.status = "absolut_ungeeignet";
            plausibilitaet.kategorie = "nutzung";

            // Detaillierte Beschreibung basierend auf Nutzung
            const landuseTypes = {
              industrial: "Industriegebiet",
              quarry: "Steinbruch",
              landfill: "Deponie",
              military: "Militärgebiet",
            };
            const landuseType = el.tags.landuse;
            plausibilitaet.fehler = `Dieser Standort liegt in einem nicht geeigneten Gebiet (${
              landuseTypes[landuseType] || landuseType
            })`;
            plausibilitaet.grund = `landuse=${landuseType}`;

            plausibilitaet.osmTags = el.tags;
            plausibilitaet.gefundeneProbleme.push({
              typ: "absolut_ungeeignet",
              kategorie: "nutzung",
              beschreibung: plausibilitaet.fehler,
              tags: el.tags,
            });

            // ABBRUCH - Standort nicht geeignet
            return {
              success: false,
              error: plausibilitaet.fehler,
              Plausibilitaet: plausibilitaet,
              Verdichtung: null,
              Bodenbelag: null,
              Nutzung: null,
              Bebauung: null,
            };
          }
        }

        // Wenn wir hier ankommen: Nicht "absolut ungeeignet"
        // Jetzt prüfen auf WARNUNGEN

        for (const el of data.elements) {
          if (!el.tags) continue;

          // LANDWIRTSCHAFT
          if (
            el.tags.landuse === "farmland" ||
            el.tags.landuse === "meadow" ||
            el.tags.landuse === "orchard" ||
            el.tags.landuse === "vineyard"
          ) {
            if (!plausibilitaet.warnung) {
              plausibilitaet.status = "warnung";
              plausibilitaet.kategorie = "landwirtschaft";

              const landuseTypes = {
                farmland: "Ackerland",
                meadow: "Wiese/Weide",
                orchard: "Obstplantage",
                vineyard: "Weinberg",
              };
              const landuseType = el.tags.landuse;
              plausibilitaet.warnung = `Landwirtschaftliche Fläche (${
                landuseTypes[landuseType] || landuseType
              }) - Genehmigung des Eigentümers erforderlich`;
              plausibilitaet.grund = `landuse=${landuseType}`;
              plausibilitaet.osmTags = el.tags;

              plausibilitaet.gefundeneProbleme.push({
                typ: "warnung",
                kategorie: "landwirtschaft",
                beschreibung: plausibilitaet.warnung,
                tags: el.tags,
              });
            }
          }

          // WALD
          if (el.tags.landuse === "forest" || el.tags.natural === "wood") {
            if (!plausibilitaet.warnung) {
              plausibilitaet.status = "warnung";
              plausibilitaet.kategorie = "wald";
              plausibilitaet.warnung =
                "Waldfläche - zusätzliche Baumpflanzung eventuell nicht sinnvoll";
              plausibilitaet.grund =
                el.tags.landuse === "forest"
                  ? "landuse=forest"
                  : "natural=wood";
              plausibilitaet.osmTags = el.tags;

              plausibilitaet.gefundeneProbleme.push({
                typ: "warnung",
                kategorie: "wald",
                beschreibung: plausibilitaet.warnung,
                tags: el.tags,
              });
            }
          }

          // ÖFFENTLICHE ANLAGEN
          if (
            el.tags.leisure === "park" ||
            el.tags.leisure === "garden" ||
            el.tags.leisure === "playground"
          ) {
            if (!plausibilitaet.warnung) {
              plausibilitaet.status = "warnung";
              plausibilitaet.kategorie = "oeffentlich";

              const leisureTypes = {
                park: "Park",
                garden: "Garten",
                playground: "Spielplatz",
              };
              const leisureType = el.tags.leisure;
              plausibilitaet.warnung = `Öffentliche Anlage (${
                leisureTypes[leisureType] || leisureType
              }) - behördliche Genehmigung erforderlich`;
              plausibilitaet.grund = `leisure=${leisureType}`;
              plausibilitaet.osmTags = el.tags;

              plausibilitaet.gefundeneProbleme.push({
                typ: "warnung",
                kategorie: "oeffentlich",
                beschreibung: plausibilitaet.warnung,
                tags: el.tags,
              });
            }
          }
        }
      }

      // ====================================================================
      // SCHRITT 3: STANDORT IST GEEIGNET (oder nur Warnung)
      //            -> Fahre mit bestehender Logik fort
      // ====================================================================

      let bodenbelag = null;
      let nutzung = null;
      let bebauung = null;

      if (data.elements) {
        data.elements.forEach((el) => {
          if (el.tags) {
            if (el.tags.surface && !bodenbelag) bodenbelag = el.tags.surface;
            if (el.tags.landuse && !nutzung) nutzung = el.tags.landuse;
            if (el.tags.building && !bebauung) bebauung = el.tags.building;
          }
        });
      }

      // Verdichtung klassifizieren (BESTEHENDE LOGIK)
      let verdichtung = "unbekannt";
      if (bodenbelag) {
        if (["asphalt", "concrete", "paving_stones"].includes(bodenbelag)) {
          verdichtung = "hoch";
        } else if (["gravel", "compacted", "ground"].includes(bodenbelag)) {
          verdichtung = "mittel";
        } else {
          verdichtung = "gering";
        }
      }

      // ====================================================================
      // SCHRITT 4: ERGEBNIS ZURÜCKGEBEN
      // ====================================================================

      return {
        success: true,
        Plausibilitaet: plausibilitaet,
        Verdichtung: verdichtung,
        Bodenbelag: bodenbelag,
        Nutzung: nutzung,
        Bebauung: bebauung,
      };
    })
    .catch((err) => {
      console.error("Overpass API Fehler:", err);
      throw err;
    });
}
